import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { dashApi } from "@/lib/dashApi";
import { useAuth } from "@/lib/auth";
import { fmtDuration, fmtNumber, fmtRelative } from "@/lib/format";
import {
  ErrorState,
  LoadingState,
  PageHeader,
  Panel,
  SourceBadge,
  StatusPill,
} from "@/components/dash/ui";

export default function IntegrationsPage() {
  const auth = useAuth();
  const qc = useQueryClient();
  const isAdmin = (auth.user?.roles ?? []).includes("admin");
  const connectors = useQuery({ queryKey: ["connectors"], queryFn: dashApi.connectors, refetchInterval: 30_000 });
  const runs = useQuery({ queryKey: ["runs"], queryFn: () => dashApi.runs() });

  const syncOne = useMutation({
    mutationFn: (code: string) => dashApi.syncOne(code),
    onSuccess: () => qc.invalidateQueries(),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Integrations"
        description="Read-only connectors to each source system. Sync to refresh snapshots on demand."
      />

      {connectors.isLoading ? <LoadingState /> : connectors.isError ? <ErrorState error={connectors.error} /> : (
        <Panel title="Connectors">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
                <th className="pb-2 font-medium">Source</th>
                <th className="pb-2 font-medium">Transport</th>
                <th className="pb-2 font-medium">Status</th>
                <th className="pb-2 text-right font-medium">Rows</th>
                <th className="pb-2 text-right font-medium">Snapshot</th>
                <th className="pb-2 text-right font-medium">Last run</th>
                {isAdmin && <th className="pb-2 text-right font-medium">Action</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-500/40">
              {connectors.data?.connectors.map((c) => (
                <tr key={c.code} className="text-slate-300">
                  <td className="py-2.5"><span className="flex items-center gap-2"><SourceBadge code={c.code} /> {c.name}</span></td>
                  <td className="py-2.5 text-slate-400">{c.transport}</td>
                  <td className="py-2.5"><StatusPill status={c.status} /></td>
                  <td className="py-2.5 text-right tabular-nums">{fmtNumber(c.rows)}</td>
                  <td className="py-2.5 text-right text-xs text-slate-500">{fmtRelative(c.captured_at)}</td>
                  <td className="py-2.5 text-right text-xs text-slate-500">{c.last_run_status ?? "—"} · {fmtDuration(c.last_run_duration_ms)}</td>
                  {isAdmin && (
                    <td className="py-2.5 text-right">
                      <button
                        type="button"
                        onClick={() => syncOne.mutate(c.code)}
                        disabled={syncOne.isPending}
                        className="rounded-md border border-ink-500/60 bg-ink-700 px-2.5 py-1 text-xs text-slate-200 hover:bg-ink-600 disabled:opacity-60"
                      >
                        Sync
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}

      <Panel title="Recent runs">
        {runs.isLoading ? <LoadingState /> : !runs.data?.runs.length ? (
          <p className="py-4 text-center text-sm text-slate-500">No runs recorded yet. Trigger a sync to populate.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500">
                <th className="pb-2 font-medium">Source</th>
                <th className="pb-2 font-medium">Type</th>
                <th className="pb-2 font-medium">Status</th>
                <th className="pb-2 text-right font-medium">Rows</th>
                <th className="pb-2 text-right font-medium">Duration</th>
                <th className="pb-2 text-right font-medium">When</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-500/40">
              {runs.data.runs.slice(0, 40).map((r) => (
                <tr key={r.id} className="text-slate-300">
                  <td className="py-2"><SourceBadge code={r.source} /></td>
                  <td className="py-2 text-slate-400">{r.run_type}</td>
                  <td className="py-2">
                    <span className={r.status === "success" ? "text-emerald-300" : r.status === "partial" ? "text-amber-300" : r.status === "failed" ? "text-rose-300" : "text-slate-400"}>
                      {r.status}
                    </span>
                  </td>
                  <td className="py-2 text-right tabular-nums">{r.rows}</td>
                  <td className="py-2 text-right text-xs text-slate-500">{fmtDuration(r.duration_ms)}</td>
                  <td className="py-2 text-right text-xs text-slate-500">{fmtRelative(r.started_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  );
}
